import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import type {
  Channel,
  SourceDeploymentConfig,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useListChannelsQuery } from '@frontend/api-sync-config';
import {
  DropdownField,
  ModalControlModalProps,
  SpinningLoader,
  Text,
  useControlledField,
} from '@frontend/design-system';
import { ActionModal, ActionModalContent } from '../shared/action-modal';
import { normalizeChannel, orderChannelsForDisplay } from '../shared/helpers';
import { TrainPicker } from '../shared/train-picker';

interface EditSourcePolicyModalProps {
  config: SourceDeploymentConfig;
  isSaving: boolean;
  modalProps: ModalControlModalProps;
  onConfirm: (channelName: string, trainId: string) => void;
}

export const EditSourcePolicyModal = ({ modalProps, isSaving, ...contentProps }: EditSourcePolicyModalProps) => (
  <ActionModal modalProps={modalProps} isSaving={isSaving} minWidth={560}>
    <EditSourcePolicyModalContent {...contentProps} isSaving={isSaving} onClose={modalProps.onClose} />
  </ActionModal>
);

type EditSourcePolicyModalContentProps = Omit<EditSourcePolicyModalProps, 'modalProps'> & { onClose: () => void };

const EditSourcePolicyModalContent = ({ config, isSaving, onClose, onConfirm }: EditSourcePolicyModalContentProps) => {
  const [selectedChannel, setSelectedChannel] = useState(config.targetChannel ?? '');
  const [selectedTrainId, setSelectedTrainId] = useState(config.targetTrainId ?? '');
  const channelFieldProps = useControlledField({
    type: 'dropdown',
    value: selectedChannel,
    onChange: setSelectedChannel,
  });
  const {
    data: channelsData,
    isLoading: isLoadingChannels,
    isError: isChannelsError,
  } = useListChannelsQuery({
    request: {},
    httpOptions: { skipValidation: true },
  });

  const channels = useMemo<Channel[]>(() => {
    const channelsByName = new Map<string, Channel>();

    for (const channelResponse of channelsData?.channels ?? []) {
      const channel = normalizeChannel(channelResponse);
      if (channel?.channelName && !channelsByName.has(channel.channelName)) {
        channelsByName.set(channel.channelName, channel);
      }
    }

    return orderChannelsForDisplay(Array.from(channelsByName.values()));
  }, [channelsData]);

  return (
    <ActionModalContent
      title='Edit Source Policy'
      onClose={onClose}
      isSaving={isSaving}
      confirmLabel='Update Policy'
      savingLabel='Saving...'
      confirmDisabled={selectedChannel === ''}
      onConfirm={() => onConfirm(selectedChannel, selectedTrainId)}
    >
      <Text color='subdued'>Select a Release Channel for the Source to be assigned to</Text>

      {isLoadingChannels ? (
        <div css={loadingStyle} role='status'>
          <SpinningLoader size='small' />
          <Text>Loading channels...</Text>
        </div>
      ) : isChannelsError ? (
        <Text color='error' role='alert'>
          Unable to load channels.
        </Text>
      ) : channels.length === 0 ? (
        <Text color='subdued'>No channels are available.</Text>
      ) : (
        <DropdownField
          {...channelFieldProps}
          label='Channel'
          name='source-policy-channel'
          placeholder='Select a channel'
          disabled={isSaving}
        >
          {channels.map((channel) => {
            const channelName = channel.channelName ?? '';

            return (
              <DropdownField.Option key={channelName} value={channelName}>
                {channelName}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      )}

      <div css={trainSelectorStyle}>
        <Text color='subdued'>Override Release Channel by pinning the Source to a Release Train</Text>
        <TrainPicker
          label='Train (optional)'
          name='source-policy-train-search'
          selectedId={selectedTrainId}
          onSelect={setSelectedTrainId}
          onClear={() => setSelectedTrainId('')}
          disabled={isSaving}
        />
      </div>
    </ActionModalContent>
  );
};

const trainSelectorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-sm);
`;

const loadingStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--fabric-space-gap-xs);
  min-height: 160px;
`;
